import React, { useState, useEffect, useMemo } from "react";
import {
  SquarePen,
  X,
  History,
  ChevronLeft,
  ChevronRight,
  Check,
  Square,
} from "lucide-react";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import { useDispatch } from "react-redux";
import { minMaxDeleteAction } from "../../../redux/actions/minMax-action";

const WIDTH_UNIT = 12; // px per "width unit"

// Table column definitions
//<Square size={15}/>
const columns = [
  { id: "select",     field: null,           label: null,       width: 5,  align: "center", type: "select" },
  { id: "storeCode",  field: "storeCode",   label: "Store Code",     width: 15, align: "center", type: "text" },
  { id: "storeName",  field: "storeName",   label: "Store Name",     width: 15, align: "left",   type: "text" },
  { id: "itemCode",   field: "itemCode",    label: "Item Code",      width: 20, align: "left",   type: "text" },
  { id: "itemName",   field: "itemName",    label: "Item Name",      width: 15, align: "left",   type: "text" },
  { id: "itemType",   field: "itemType",    label: "Item Type",      width: 15, align: "left",   type: "text" },
  { id: "itemCategory",field: "itemCategory",label: "Item Category", width: 15, align: "left",   type: "text" },
  { id: "itemGroup",  field: "itemGroup",   label: "Item Group",     width: 15, align: "left",   type: "text" },

  { id: "avgSales30D", field: "avgSales30D", label: "AVG Sales 30D",  width: 8,  align: "center", type: "text" },
  { id: "avgSales60D", field: "avgSales60D", label: "AVG Sales 60D",  width: 8,  align: "center", type: "text" },
  { id: "avgSales90D", field: "avgSales90D", label: "AVG Sales 90D",  width: 8,  align: "center", type: "text" },
  { id: "avgSales180D",field: "avgSales180D",label: "AVG Sales 180D", width: 8,  align: "center", type: "text" },

  { id: "exMinQty",    field: "exMinQty",    label: "Ex Min Qty",     width: 8,  align: "center", type: "text" },
  { id: "exMaxQty",    field: "exMaxQty",    label: "Ex Max Qty",     width: 8,  align: "center", type: "text" },

  { id: "sugMinQty",   field: "sugMinQty",   label: "Sug Min Qty",    width: 8,  align: "center", type: "input" },
  { id: "sugMaxQty",   field: "sugMaxQty",   label: "Sug Max Qty",    width: 8,  align: "center", type: "input" },

  { id: "deviation",   field: "deviationLabel", label: "Deviation",   width: 5,  align: "center", type: "deviation" },
  { id: "action",      field: null,          label: "Action",         width: 15, align: "center", type: "actions" },
];

const totalTableWidthPx = columns.reduce(
  (sum, col) => sum + col.width * WIDTH_UNIT,
  0
);

// Tooltip wrapper for icons
function IconWithTooltip({ label, children, onClick, disabled }) {
  return (
    <div className="relative group flex items-center justify-center">
      <button
        className={`hover:opacity-70 focus:outline-none ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        type="button"
        aria-label={label}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
      >
        {children}
      </button>

      <div
        className="
          pointer-events-none
          absolute -top-7 left-1/2 -translate-x-1/2
          whitespace-nowrap rounded-md px-2 py-1 text-[10px]
          bg-black text-white
          opacity-0 translate-y-1
          group-hover:opacity-100 group-hover:translate-y-0
          transition-all duration-150
          shadow-lg
          z-30
        "
      >
        {label}
        <div
          className="
            absolute left-1/2 top-full -translate-x-1/2
            w-2 h-2 bg-black rotate-45
          "
        />
      </div>
    </div>
  );
}

export default function InventoryTable({
  data = [],
  setFieldValue,
  rowsPerPage,
  setRowsPerPage,
  currentPage,
  setCurrentPage,
  onClickRunanyalsis
}) {
  const dispatch = useDispatch();
  const [inventoryValuesByItemId, setInventoryValuesByItemId] = useState({});
  const [editingByItemId, setEditingByItemId] = useState({});
  const [selectedItemsByItemId, setSelectedItemsByItemId] = useState({});

  const rawByItemId = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      if (item.item_id) {
        map[item.item_id] = item;
      }
    });
    return map;
  }, [data]);

  const baseRows = data.map((item, index) => {
    const deviationValue = Number(item.deviation ?? 0);
    const isPositive = deviationValue >= 0;

    return {
      _originalIndex: index,
      itemId: item.item_id,

      storeCode: item.pos_id,
      storeName: item?.pos_name || item?.store_name || item.pos_id,
      itemCode: item.item_code,
      itemName: item.item_name,
      itemType: item?.item_type || "",
      itemCategory: item?.item_category || "",
      itemGroup: item?.item_group || "",

      avgSales30D: Number(item.avg_sales_30 ?? 0),
      avgSales60D: Number(item.avg_sales_60 ?? 0),
      avgSales90D: Number(item.avg_sales_90 ?? 0),
      avgSales180D: Number(item.avg_sales_180 ?? 0),

      exMinQty: Number(item.cur_item_min_quantity ?? 0),
      exMaxQty: Number(item.cur_item_max_quantity ?? 0),

      sugMinQty: Number(item.suggested_min_qty ?? 0),
      sugMaxQty: Number(item.suggested_max_qty ?? 0),

      deviationLabel: `${isPositive ? "+" : "-"}${Math.abs(deviationValue)}%`,
      deviationText: isPositive ? "text-[#5ca55e]" : "text-[#c03a32]",
      deviationBg: isPositive ? "bg-[#e5fbe9]" : "bg-[#f7e3e3]",
    };
  });

  const allData = baseRows.map((row) => {
    const overrides = inventoryValuesByItemId[row.itemId];
    return overrides ? { ...row, ...overrides } : row;
  });

  useEffect(() => {
    if (typeof setFieldValue !== "function") return;

    const uiRows = Object.values(inventoryValuesByItemId).sort(
      (a, b) => (a._originalIndex ?? 0) - (b._originalIndex ?? 0)
    );

    const inventoryArray = uiRows.map((uiRow) => {
      const base = rawByItemId[uiRow.itemId] || {};
      return {
        ...base,
        suggested_min_qty: String(
          uiRow.sugMinQty ?? base.suggested_min_qty ?? ""
        ),
        suggested_max_qty: String(
          uiRow.sugMaxQty ?? base.suggested_max_qty ?? ""
        ),
      };
    });

    setFieldValue("inventoryValues", inventoryArray);
  }, [inventoryValuesByItemId, setFieldValue]);

  const totalPages = Math.max(1, Math.ceil(allData.length / rowsPerPage));

  const paginatedData = allData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((p) => p - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  };

  const handleEditRow = (row) => {
    const itemId = row.itemId;
    if (!itemId) return;

    setInventoryValuesByItemId((prev) => {
      if (prev[itemId]) {
        return prev;
      }
      return {
        ...prev,
        [itemId]: { ...row },
      };
    });

    setEditingByItemId((prev) => ({
      ...prev,
      [itemId]: true,
    }));
  };

  const handleSaveRow = (row) => {
    const itemId = row.itemId;
    if (!itemId) return;

    setEditingByItemId((prev) => {
      if (!prev[itemId]) return prev;
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
    successToast("Qty updated successfully");
  };

  const handleInputChange = (itemId, field, value, row) => {
    setInventoryValuesByItemId((prev) => {
      const base = prev[itemId] || row;
      return {
        ...prev,
        [itemId]: {
          ...base,
          [field]: value,
        },
      };
    });
  };

  const handleUndoRow = (row) => {
    const itemId = row.itemId;
    if (!itemId) return;

    setInventoryValuesByItemId((prev) => {
      if (!prev[itemId]) return prev;
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });

    setEditingByItemId((prev) => {
      if (!prev[itemId]) return prev;
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const handleToggleRowSelect = (row, checked) => {
    const itemId = row.itemId;
    if (!itemId) return;

    setSelectedItemsByItemId((prev) => {
      const next = { ...prev };
      if (checked) {
        next[itemId] = row;
      } else {
        delete next[itemId];
      }
      return next;
    });
  };

  const baseCellClass =
    "px-3 py-2 text-sm text-gray-800 align-middle whitespace-normal break-words";

  const startIndex =
    allData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIndex =
    allData.length === 0 ? 0 : Math.min(currentPage * rowsPerPage, allData.length);

  return (
    <div
      className="w-full border border-[var(--button-border-color)] rounded-md overflow-hidden flex flex-col bg-white"
      style={{ minHeight: "37vh", maxHeight: "36vh" }}
    >
      <div className="overflow-y-auto flex-1">
        <div className="overflow-x-auto">
          <table
            className="table-fixed border-collapse text-sm"
            style={{
              width: totalTableWidthPx,
              minWidth: totalTableWidthPx,
            }}
          >
            <colgroup>
              {columns.map((col) => {
                const colWidthPx = col.width * WIDTH_UNIT;
                return (
                  <col
                    key={col.id}
                    style={{
                      width: colWidthPx,
                      minWidth: colWidthPx,
                      maxWidth: colWidthPx,
                    }}
                  />
                );
              })}
            </colgroup>

            <thead className="bg-[var(--list-table-heading-bg)] text-white sticky top-0 z-20">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={`${baseCellClass} ${
                      col.align === "center" ? "text-center" : "text-left"
                    }`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-6 text-center text-sm text-[var(--description-color)]"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, rowIndex) => {
                  const itemId = row.itemId;
                  const isEditing = !!editingByItemId[itemId];
                  const hasCommittedValue = !!inventoryValuesByItemId[itemId];
                  const isSelected = !!selectedItemsByItemId[itemId];

                  return (
                    <tr
                      key={itemId ?? rowIndex}
                      className="border-b border-[var(--button-border-color)]"
                    >
                      {columns.map((col) => {
                        const alignClass =
                          col.align === "center" ? "text-center" : "text-left";

                        if (col.type === "select") {
                          return (
                            <td
                              key={col.id}
                              className={`${baseCellClass} ${alignClass}`}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={isSelected}
                                onChange={(e) =>
                                  handleToggleRowSelect(row, e.target.checked)
                                }
                              />
                            </td>
                          );
                        }

                        if (col.type === "input") {
                          return (
                            <td
                              key={col.id}
                              className={`${baseCellClass} ${alignClass}`}
                            >
                              <input
                                type="number"
                                value={row[col.field] ?? ""}
                                disabled={!isEditing}
                                onChange={(e) =>
                                  isEditing &&
                                  handleInputChange(
                                    itemId,
                                    col.field,
                                    e.target.value,
                                    row
                                  )
                                }
                                className={`w-16 text-center border border-primary rounded-lg text-gray-700 py-2 ${
                                  isEditing
                                    ? "bg-white border-blue-500"
                                    : "bg-[#f1f6fe]"
                                }`}
                              />
                            </td>
                          );
                        }

                        if (col.type === "deviation") {
                          return (
                            <td
                              key={col.id}
                              className={`${baseCellClass} ${alignClass} font-semibold ${row.deviationText}`}
                            >
                              <div
                                className={`${row.deviationBg} py-1 px-2 rounded-[30px] inline-block`}
                              >
                                {row.deviationLabel}
                              </div>
                            </td>
                          );
                        }

                        if (col.type === "actions") {
                          return (
                            <td
                              key={col.id}
                              className={`${baseCellClass} ${alignClass}`}
                            >
                              <div className="flex items-center justify-center gap-2">
                                <IconWithTooltip
                                  label={isEditing ? "Save" : "Edit"}
                                  onClick={() =>
                                    isEditing
                                      ? handleSaveRow(row)
                                      : handleEditRow(row)
                                  }
                                >
                                  {isEditing ? (
                                    <Check size={16} stroke="#22c55e" />
                                  ) : (
                                    <SquarePen size={16} stroke="#577df5" />
                                  )}
                                </IconWithTooltip>

                                <IconWithTooltip
                                  label="Remove"
                                  onClick={() => {
                                    if (isEditing) {
                                      // Exit edit and undo changes
                                      handleUndoRow(row);
                                      return;
                                    }
                                    // Call delete API when not editing
                                    const payload = {
                                      pos_id: row.storeCode,
                                      item_id: row.itemId,
                                    };
                                    dispatch(
                                      minMaxDeleteAction(
                                        payload,
                                        (res) => {
                                          successToast(res?.message || "Deleted successfully");
                                          if (typeof onClickRunanyalsis === "function") {
                                            onClickRunanyalsis();
                                          }
                                        },
                                        (err) => {
                                          errorToast(err?.data?.message || err?.message || "Delete failed");
                                        }
                                      )
                                    );
                                  }}
                                >
                                  <X size={16} stroke="#ff0000" />
                                </IconWithTooltip>

                                <IconWithTooltip
                                  label="Undo"
                                  onClick={() => handleUndoRow(row)}
                                  disabled={!hasCommittedValue}
                                >
                                  <History
                                    size={16}
                                    stroke={
                                      hasCommittedValue ? "#cf612c" : "#cccccc"
                                    }
                                  />
                                </IconWithTooltip>
                              </div>
                            </td>
                          );
                        }

                        return (
                          <td
                            key={col.id}
                            className={`${baseCellClass} ${alignClass}`}
                          >
                            {row[col.field]}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-2 text-sm text-[var(--description-color)] sticky bottom-0 z-20 bg-white">
        <span>
          Showing{" "}
          <strong>
            {startIndex}-{endIndex}
          </strong>{" "}
          of <strong>{allData.length}</strong>
        </span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <select
              className="border border-[var(--button-border-color)] rounded-lg px-2 py-1 text-sm bg-white"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>Rows / Page</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              className={`p-1 border border-gray-300 rounded-sm hover:bg-gray-200 ${
                currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={() => handlePageChange("prev")}
              disabled={currentPage === 1}
              type="button"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className={`p-1 border border-gray-300 rounded-sm hover:bg-gray-200 ${
                data.length < rowsPerPage
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={() => handlePageChange("next")}
              disabled={data.length < rowsPerPage}
              type="button"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
